import { invoices } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { withAttachments } from './attachment'

// The caller's local calendar day ('YYYY-MM-DD'), used as the reference point
// for overdue calculations. The frontend computes it so the day boundary
// follows the user's timezone; the backend applies the business grace period
// (the grace lives in @markku/utils: getOverdueCutoffDate / isOverdueAsOf).
export const overdueAsOfInputSchema = z.object({
  asOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'asOf must be YYYY-MM-DD'),
})
export type OverdueAsOfInput = z.infer<typeof overdueAsOfInputSchema>

export const selectInvoiceSchema = createSelectSchema(invoices)
export const selectInvoiceWithAttachmentsSchema =
  withAttachments(selectInvoiceSchema)

export const insertInvoiceSchema = createInsertSchema(invoices).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectInvoiceDetailSchema =
  selectInvoiceWithAttachmentsSchema.extend({
    visit: z
      .object({
        startTime: z.string(),
        property: z
          .object({
            id: z.number(),
            customer: z
              .object({
                id: z.number(),
                name: z.string().nullable(),
              })
              .nullable(),
            address: z
              .object({
                street: z.string().nullable(),
                city: z.string().nullable(),
              })
              .nullable(),
          })
          .nullable(),
      })
      .nullable(),
    invoicePayments: z.array(
      z.object({
        id: z.number(),
        amount: z.string(),
        paidAt: z.string(),
        archiveId: z.string(),
        referenceNumber: z.string(),
        payerName: z.string().nullable(),
      }),
    ),
    invoiceLines: z.array(
      z.object({
        id: z.number(),
        taskName: z.string(),
        price: z.string().nullable(),
        discountPercent: z.number().nullable(),
        billingType: z.string().nullable(),
      }),
    ),
    invoiceMaterialLines: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
        type: z.string(),
        quantity: z.number(),
        unitPriceVat0: z.string().nullable(),
        billingType: z.string(),
      }),
    ),
  })

export type Invoice = z.infer<typeof selectInvoiceSchema>
export type InvoiceWithAttachments = z.infer<
  typeof selectInvoiceWithAttachmentsSchema
>
export type InvoiceDetail = z.infer<typeof selectInvoiceDetailSchema>
export type CreateInvoice = z.infer<typeof insertInvoiceSchema>
